import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import store from '../store';
import { logout, setTokens } from '../store/authSlice';

/**
 * Extract a human-readable error message from a value thrown by an axios call.
 * Prefers the backend's `detail` field (FastAPI convention), then `message`,
 * then axios's own error message, then the provided fallback.
 */
export function extractErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { detail?: string; message?: string } | undefined;
    return data?.detail || data?.message || err.message || fallback;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';

const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const EMAIL_AUTH_TIMEOUT_MS = 20000;

// Request interceptor
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle token refresh on 401
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          });
          const { access_token, refresh_token: newRefresh } = response.data;
          store.dispatch(setTokens({ token: access_token, refreshToken: newRefresh || refreshToken }));
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        } catch {
          // Refresh failed — clear tokens and redirect to login
          store.dispatch(logout());
          window.location.href = '/login';
        }
      } else {
        store.dispatch(logout());
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// Auth endpoints
export const authApi = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),

  register: (firstName: string, lastName: string, email: string, password: string, inviteCode?: string) =>
    api.post(
      '/auth/register',
      { email, password, full_name: `${firstName} ${lastName}`, invite_code: inviteCode },
      { timeout: EMAIL_AUTH_TIMEOUT_MS }
    ),

  verifyEmail: (email: string, code: string) =>
    api.post('/auth/verify-email', { email, code }),

  resendCode: (email: string) =>
    api.post('/auth/resend-code', { email }, { timeout: EMAIL_AUTH_TIMEOUT_MS }),

  forgotPassword: (email: string) =>
    api.post('/auth/forgot-password', { email }, { timeout: EMAIL_AUTH_TIMEOUT_MS }),

  resetPassword: (email: string, code: string, newPassword: string) =>
    api.post('/auth/reset-password', { email, code, new_password: newPassword }),

  refreshToken: (refreshToken: string) =>
    api.post('/auth/refresh', { refresh_token: refreshToken }),

  logout: (refreshToken?: string) =>
    api.post('/auth/logout', { refresh_token: refreshToken }),
};

// User endpoints
export const userApi = {
  getProfile: () => api.get('/auth/me'),

  updateProfile: (data: Record<string, unknown>) => api.put('/auth/me', data),

  changePassword: (currentPassword: string, newPassword: string) =>
    api.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    }),

  deleteAccount: () => api.delete('/auth/me'),
};

// Document endpoints
export const documentApi = {
  upload: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/documents/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  getDocuments: (projectId?: number) =>
    api.get('/documents/list', { params: projectId ? { project_id: projectId } : {} }),

  getDocument: (id: string) => api.get(`/documents/${id}`),

  deleteDocument: (id: string) => api.delete(`/documents/${id}`),

  assignDocument: (docId: number, projectId: number, personId?: number) =>
    api.put(`/documents/${docId}/assign`, null, {
      params: { project_id: projectId, ...(personId ? { person_id: personId } : {}) },
    }),

  getThumbnail: (docId: number) => api.get(`/documents/${docId}/thumbnail`, { responseType: 'blob' }),
};

// Project endpoints
export const projectsApi = {
  list: () => api.get('/projects/'),

  get: (id: number) => api.get(`/projects/${id}`),

  create: (name: string, taxYear: number, persons: Array<{ name: string; relationship: string }>, filingStatus?: string) =>
    api.post('/projects/', { name, tax_year: taxYear, filing_status: filingStatus, persons }),

  update: (id: number, data: { name?: string; filing_status?: string }) =>
    api.put(`/projects/${id}`, data),

  delete: (id: number) => api.delete(`/projects/${id}`),

  addPerson: (projectId: number, name: string, relationship: string) =>
    api.post(`/projects/${projectId}/persons`, { name, relationship }),

  removePerson: (projectId: number, personId: number) =>
    api.delete(`/projects/${projectId}/persons/${personId}`),
};

// Questionnaire endpoints
export const questionnaireApi = {
  saveAnswers: (taxYear: number, filingStatus: string, answers: object) =>
    api.post(`/tax-profile/questionnaire?tax_year=${taxYear}`, {
      filing_status: filingStatus,
      questionnaire_data: answers,
    }),
  getAnswers: (taxYear: number) =>
    api.get(`/tax-profile/${taxYear}`),
  getSmartDefaults: (taxYear: number) =>
    api.get(`/tax-profile/smart-defaults/${taxYear}`),
};

// Document analysis endpoints
export const documentAnalysisApi = {
  classifyDocument: (documentId: number) =>
    api.post(`/analysis/classify/${documentId}`),

  analyzeDocument: (documentId: number) =>
    api.post(`/analysis/document/${documentId}`),

  getDocumentAnalysis: (documentId: number) =>
    api.get(`/analysis/document/${documentId}/result`),

  analyzeFullReturn: (taxYear: number, filingStatus: string = 'single', questionnaireData: object = {}, projectId?: number | null) =>
    api.post(`/analysis/full-return/${taxYear}`, {
      tax_year: taxYear,
      filing_status: filingStatus,
      questionnaire_data: questionnaireData,
      ...(projectId ? { project_id: projectId } : {}),
    }),

  getTaxSuggestions: (taxYear: number) =>
    api.get(`/analysis/suggestions/${taxYear}`),

  verifyReturn: (
    taxYear: number,
    analysisId: number,
    corrections: Array<{
      doc_index: number;
      field_name: string;
      extracted_value: number;
      corrected_value: number | null;
    }>,
    skipVerification = false,
  ) =>
    api.post(`/analysis/verify/${taxYear}`, {
      analysis_id: analysisId,
      corrections,
      skip_verification: skipVerification,
    }),
};

// Canonical TaxFacts endpoints
export const taxFactsApi = {
  getProjectFacts: (projectId: number) => api.get(`/tax-facts/projects/${projectId}`),

  updateValues: (
    projectId: number,
    values: Array<{
      field_key: string;
      value: string | number | boolean | null;
      occurrence_key?: string;
      verification_status?: string;
    }>
  ) => api.patch(`/tax-facts/projects/${projectId}/values`, { values }),

  recalculate: (projectId: number) => api.post(`/tax-facts/projects/${projectId}/recalculate`),
};

// Tax form endpoints
export const taxFormsApi = {
  generateForms: (taxYear: number) => api.post(`/tax-forms/generate/${taxYear}`),
  listForms: (taxYear: number) => api.get(`/tax-forms/list/${taxYear}`),
  downloadForm: (formId: number) => api.get(`/tax-forms/download/${formId}`, { responseType: 'blob' }),
  getLatestForms: (taxYear: number) => api.get(`/tax-forms/latest/${taxYear}`),
};

// AI Profile endpoints
export const aiProfileApi = {
  getProfile: () => api.get('/ai-profile'),
  updateProfile: (data: Record<string, unknown>) => api.put('/ai-profile', data),
  getLog: () => api.get('/ai-profile/log'),
  resetProfile: () => api.post('/ai-profile/reset'),
};

// Knowledge base endpoints
export const knowledgeApi = {
  getUpdates: (limit = 5) => api.get(`/knowledge/updates?limit=${limit}`),
  getStatus: () => api.get('/knowledge/status'),
  getArticles: (category?: string) => api.get('/knowledge/articles', { params: { category } }),
};

export const feedbackApi = {
  submit: (payload: {
    step: string;
    what_happened: string;
    expected_behavior?: string;
    document_types?: string[];
    is_blocker?: boolean;
    page?: string;
    metadata?: Record<string, unknown>;
  }) => api.post('/feedback', payload),
};

export default api;
